import type {
  StateTransition,
  StateTransitionIssue,
  StateTransitionReport,
  StateWrite,
} from "./state-transition-analyzer"

/**
 * state-transition-proof - Phase 3 of the ActionFlow state-machine validation
 * plan (docs/agentic/actionflow-state-machine-validation-plan.md, super-repo).
 *
 * Pure adapter: matches the analyzer's EXPECTED writes against ACTUAL runtime
 * trace state diffs and emits `state-machine-trace-mismatch` (warning) issues.
 * Duck-typed trace input so this package depends on neither @cogo/runtime-v2
 * nor Studio types - Studio's ActionFlowTraceEntry maps onto it with a
 * 3-field pick, runtime-v2's ActionFlowExecutionTrace via its startState/
 * finalState leaf diff.
 *
 * NEVER imported by the runtime execution path (validation/inspection only).
 */
export interface TransitionTraceStateDiff {
  path: string
  before?: unknown
  after?: unknown
}

export interface TransitionTraceInput {
  flowName: string
  status: "pass" | "error"
  stateDiffs: TransitionTraceStateDiff[]
}

export type TransitionProofStatus = "proven" | "mismatch" | "unproven"

export interface TransitionProof {
  transitionId: string
  flow: string
  path: string
  status: TransitionProofStatus
  traceStatus?: "pass" | "error"
}

export interface StateTransitionProofReport {
  proofs: TransitionProof[]
  issues: StateTransitionIssue[]
}

const RUNTIME_INTERNAL_PREFIXES = ["_results.", "computed."]
const RUNTIME_INTERNAL_PATHS = new Set(["appData.lastAction"])

export function proveStateTransitions(
  report: StateTransitionReport,
  traces: TransitionTraceInput[],
): StateTransitionProofReport {
  const proofs: TransitionProof[] = []
  const issues: StateTransitionIssue[] = []

  for (const transition of report.transitions) {
    for (const write of transition.writes) {
      proofs.push(proveWrite(transition, write, report.transitions, traces, issues))
    }
  }

  collectUnexpectedWriteIssues(report.transitions, traces, issues)

  return { proofs, issues }
}

function proveWrite(
  transition: StateTransition,
  write: StateWrite,
  allTransitions: StateTransition[],
  traces: TransitionTraceInput[],
  issues: StateTransitionIssue[],
): TransitionProof {
  const base = { transitionId: transition.id, flow: transition.flow, path: write.path }
  const flowTraces = traces.filter((trace) => trace.flowName === transition.flow)

  if (flowTraces.length === 0) return { ...base, status: "unproven" }

  const matching = flowTraces.find((trace) =>
    trace.stateDiffs.some((diff) => pathsOverlap(diff.path, write.path)),
  )
  if (matching) {
    // The trace input has no step identity, so a diff cannot tell WHICH branch
    // produced it. A guarded write competing with another transition under a
    // different guard (or an unguarded sibling) for an overlapping path is
    // ambiguous evidence — it stays unproven instead of being "proven" by a
    // diff the other branch may have produced (2026-06-11 review F4). An
    // unguarded write runs unconditionally, so the diff is always consistent
    // with it and it remains proven.
    if (transition.guard && hasCompetingWriter(transition, write, allTransitions)) {
      return { ...base, status: "unproven" }
    }
    return { ...base, status: "proven", traceStatus: matching.status }
  }

  // The flow executed but this write never showed up. A guarded transition may
  // simply not have taken that branch - degrade to unproven, never mismatch.
  if (transition.guard) return { ...base, status: "unproven" }

  issues.push({
    rule: "state-machine-trace-mismatch",
    level: "warning",
    message: `Flow '${transition.flow}' executed at runtime but expected write '${write.path}' was not observed in any trace`,
    flow: transition.flow,
    stepPath: transition.stepPath,
    path: write.path,
  })
  return { ...base, status: "mismatch" }
}

function hasCompetingWriter(
  transition: StateTransition,
  write: StateWrite,
  allTransitions: StateTransition[],
): boolean {
  return allTransitions.some((other) =>
    other !== transition &&
    other.flow === transition.flow &&
    (other.guard ?? "") !== (transition.guard ?? "") &&
    other.writes.some((otherWrite) => pathsOverlap(otherWrite.path, write.path)),
  )
}

function collectUnexpectedWriteIssues(
  transitions: StateTransition[],
  traces: TransitionTraceInput[],
  issues: StateTransitionIssue[],
): void {
  const flowsWithTransitions = new Set(transitions.map((transition) => transition.flow))
  const reported = new Set<string>()

  for (const trace of traces) {
    if (!flowsWithTransitions.has(trace.flowName)) continue
    const expectedPaths = transitions
      .filter((transition) => transition.flow === trace.flowName)
      .flatMap((transition) => transition.writes.map((write) => write.path))

    for (const diff of trace.stateDiffs) {
      if (isRuntimeInternalPath(diff.path)) continue
      if (expectedPaths.some((path) => pathsOverlap(path, diff.path))) continue
      const key = `${trace.flowName}|${diff.path}`
      if (reported.has(key)) continue
      reported.add(key)
      issues.push({
        rule: "state-machine-trace-mismatch",
        level: "warning",
        message: `Runtime wrote '${diff.path}' in flow '${trace.flowName}' but no static transition predicts it`,
        flow: trace.flowName,
        path: diff.path,
      })
    }
  }
}

function isRuntimeInternalPath(path: string): boolean {
  if (RUNTIME_INTERNAL_PATHS.has(path)) return true
  return RUNTIME_INTERNAL_PREFIXES.some((prefix) => path.startsWith(prefix))
}

function pathsOverlap(a: string, b: string): boolean {
  return a === b || a.startsWith(`${b}.`) || b.startsWith(`${a}.`)
}
